import { hoyLocal } from './fechaLocal';
import { sumarDias, proximoDiaSemana } from './tareasFechas';
import type { Assignee, TaskPriority } from '../components/tasks/types';

/**
 * Alta rápida de tareas: de una línea escrita saca responsable, fecha, hora y
 * prioridad, y deja el título limpio.
 *
 *   "Llamar a Ponsuar por el service @rodrigo mañana 10:00 !alta"
 *   → título "Llamar a Ponsuar por el service", Rodrigo, mañana 10:00, alta
 *
 * Fichas: @nombre · hoy / mañana / pasado / lunes..domingo / 20/09 · 15:00 ·
 * !alta !urgente !normal
 */
export interface TareaRapida {
  title: string;
  assigneeId?: string;
  assigneeName?: string;
  dueYmd?: string;
  dueTime?: string;
  priority: TaskPriority;
}

// Marcas diacríticas (U+0300 a U+036F) sin escribir la barra en el código fuente
const MARCAS = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');
const sinAcentos = (s: string) => s.normalize('NFD').replace(MARCAS, '').toLowerCase();

const DIAS: Record<string, number> = {
  domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6
};

export function parsearTareaRapida(input: string, assignees: Assignee[] = [], hoy: string = hoyLocal()): TareaRapida {
  let texto = ` ${input.trim()} `;
  const out: TareaRapida = { title: '', priority: 'normal' };

  // @responsable: matchea por nombre, apellido o usuario (sin acentos)
  texto = texto.replace(/\s@([\p{L}\d._-]+)/gu, (m, nombre: string) => {
    const q = sinAcentos(nombre);
    const hit = assignees.find((a) => sinAcentos(a.name).split(/\s+/).some((p) => p.startsWith(q)))
      || assignees.find((a) => sinAcentos(a.name).replace(/\s+/g, '').startsWith(q));
    if (!hit) return m;
    out.assigneeId = hit.id;
    out.assigneeName = hit.name;
    return ' ';
  });

  // !prioridad
  texto = texto.replace(/\s!(urgente|alta|normal|baja)\b/i, (_m, p: string) => {
    const k = sinAcentos(p);
    out.priority = k === 'urgente' ? 'urgent' : k === 'alta' ? 'high' : k === 'baja' ? 'low' : 'normal';
    return ' ';
  });

  // hora 15:00 / 15hs / 15h
  texto = texto.replace(/\s(\d{1,2})(?::(\d{2})|\s?hs?\b)/i, (m, h: string, mi?: string) => {
    const hh = Number(h);
    if (hh > 23) return m;
    out.dueTime = `${String(hh).padStart(2, '0')}:${mi || '00'}`;
    return ' ';
  });

  // fecha dd/mm o dd/mm/aaaa
  texto = texto.replace(/\s(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/, (m, d: string, mo: string, y?: string) => {
    const dd = Number(d), mm = Number(mo);
    if (dd < 1 || dd > 31 || mm < 1 || mm > 12) return m;
    let yy = y ? Number(y) : Number(hoy.slice(0, 4));
    if (yy < 100) yy += 2000;
    out.dueYmd = `${yy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    return ' ';
  });

  // palabras de fecha
  texto = texto.replace(/\s(hoy|ma[ñn]ana|pasado(?:\s+ma[ñn]ana)?|lunes|martes|mi[eé]rcoles|jueves|viernes|s[aá]bado|domingo)\b/i, (m, w: string) => {
    if (out.dueYmd) return m;
    const k = sinAcentos(w.replace(/\s+/g, ' '));
    if (k === 'hoy') out.dueYmd = hoy;
    else if (k === 'manana') out.dueYmd = sumarDias(hoy, 1);
    else if (k.startsWith('pasado')) out.dueYmd = sumarDias(hoy, 2);
    else if (DIAS[k] !== undefined) out.dueYmd = proximoDiaSemana(hoy, DIAS[k]);
    else return m;
    return ' ';
  });

  // "el" / "para" / "a las" que quedan sueltos al final: "Llamar a Ponsuar el" → "Llamar a Ponsuar"
  out.title = texto
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s+(el|para|para el|a las|a la)$/i, '')
    .trim();
  if (out.dueTime && !out.dueYmd) out.dueYmd = hoy;
  return out;
}

export const resumenTareaRapida = (p: TareaRapida, hoy: string = hoyLocal()): string => {
  const partes: string[] = [];
  if (p.assigneeName) partes.push(p.assigneeName.split(' ')[0]);
  if (p.dueYmd) {
    const cuando = p.dueYmd === hoy ? 'hoy' : p.dueYmd === sumarDias(hoy, 1) ? 'mañana' : `${p.dueYmd.slice(8, 10)}/${p.dueYmd.slice(5, 7)}`;
    partes.push(p.dueTime ? `${cuando} ${p.dueTime}` : cuando);
  }
  if (p.priority === 'urgent') partes.push('urgente');
  else if (p.priority === 'high') partes.push('alta');
  return partes.join(' · ');
};
